import type { ReactNode } from 'react'

import type { ResourceStatus, StatusLabels, StatusOrder } from '@/shared/lib/status'

export type { FilterOption, ListItem } from './types'

/* `icon?: ReactNode` e não `unknown`: o retorno alimenta `FilterChips`
   diretamente, e `unknown` não é atribuível a `ReactNode`. Este módulo é puro —
   importa só o tipo, nunca runtime de React. */
type Option<T extends string> = { id: T; label: string; icon?: ReactNode }

/**
 * Remove da barra os chips que não têm item correspondente.
 *
 * Motivação: antes, todos os chips eram renderizados sempre, então uma listagem
 * com três jornadas em andamento exibia "Concluídas", "Bloqueadas" e "Novas"
 * clicáveis para lista vazia. Filtro que só leva a nada é ruído.
 *
 * `matches` é injetado porque **filtro não é sinônimo de status**: `/missoes` tem
 * `ending` (janela de 24h) e `recommended` (top-3 por recompensa), e `new` é
 * recência. Uma implementação que comparasse `item.status === id` não cobriria
 * nenhum dos três.
 *
 * `all` sobrevive sempre que a lista tiver algum item (R3.2): é o estado default
 * e, sem ele, um filtro que perdeu seus itens deixaria o usuário sem volta.
 */
export const visibleFilterOptions = <T extends string>(
  options: Option<T>[],
  matches: (id: T) => boolean,
  { isEmpty = false, allId = 'all' as T }: { isEmpty?: boolean; allId?: T } = {}
): Option<T>[] => {
  if (isEmpty) return []

  return options.filter(option => option.id === allId || matches(option.id))
}

/**
 * Resolve o filtro que de fato vale para esta renderização.
 *
 * Cenário: o usuário filtra por "Novas", passa a janela de 1 hora, o item deixa
 * de ser novo e o chip desaparece — restaria uma lista vazia sem chip para
 * clicar de volta.
 *
 * É **derivação, não efeito**. Um `useEffect` + `setFilter` produziria um render
 * intermediário com a lista vazia antes de corrigir.
 */
export const effectiveFilter = <T extends string>(
  filter: T,
  visible: Option<T>[],
  { allId = 'all' as T }: { allId?: T } = {}
): T => (visible.some(option => option.id === filter) ? filter : allId)

export type StatusGroup<I> = {
  status: ResourceStatus
  label: string
  items: I[]
}

type GroupOptions<I> = {
  order: StatusOrder
  labels: StatusLabels
  /** Acessor porque as features nomeiam o campo `uiStatus`, não `status`. */
  statusOf: (item: I) => ResourceStatus
  /** Item já renderizado fora das seções (card em destaque) — não duplicar. */
  exclude?: (item: I) => boolean
}

/**
 * Agrupa itens por status, na ordem canônica, devolvendo só grupos não-vazios.
 *
 * A tabela de ordem é a mesma que ordena os itens dentro de um filtro — sendo a
 * mesma fonte, a ordem das seções e a dos itens não podem divergir (R1.4).
 *
 * Antes disto, cada listagem montava à mão um objeto `grouped` com chaves
 * próprias (`participating`, `inProgress`, `available`, `won`), e nenhuma delas
 * concordava com as outras.
 */
export const groupByStatus = <I>(
  items: I[],
  { order, labels, statusOf, exclude }: GroupOptions<I>
): StatusGroup<I>[] => {
  const buckets = new Map<ResourceStatus, I[]>()

  for (const item of items) {
    if (exclude?.(item)) continue

    const status = statusOf(item)
    const bucket = buckets.get(status)

    if (bucket) bucket.push(item)
    else buckets.set(status, [item])
  }

  /* `Array.from` e não spread do iterador: o tsconfig deste projeto não liga
     `downlevelIteration`, e iterar `Map.entries()` com spread não compila. */
  return Array.from(buckets.entries())
    .map(([status, groupItems]) => ({
      status,
      label: labels[status],
      items: groupItems
    }))
    .sort((a, b) => order[a.status] - order[b.status])
}
